import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";
import { complianceApi, ComplianceApiError } from "./compliance-api";
import { queryRetry } from "./query-retry";

export const merchantCasesInfiniteQueryKeyPrefix = [
  "compliance",
  "merchant",
  "cases",
  "infinite",
] as const;
export const adminCasesInfiniteQueryKeyPrefix = [
  "compliance",
  "admin",
  "cases",
  "infinite",
] as const;

export const complianceQueryKeys = {
  merchantRequiringActionCount: (applicationId: string) =>
    [
      "compliance",
      "merchant",
      "cases",
      "requiring-action-count",
      applicationId,
    ] as const,
  merchantCases: (params: Record<string, unknown>) =>
    ["compliance", "merchant", "cases", params] as const,
  merchantCase: (caseId: string) =>
    ["compliance", "merchant", "case", caseId] as const,
  merchantPaymentCase: (applicationId: string, paymentId: string) =>
    [
      "compliance",
      "merchant",
      "payment-case",
      applicationId,
      paymentId,
    ] as const,

  adminOverview: () => ["compliance", "admin", "overview"] as const,
  adminOverviewWorkflowStats: () =>
    ["compliance", "admin", "overview", "workflow-stats"] as const,
  adminCases: (params: Record<string, unknown>) =>
    ["compliance", "admin", "cases", params] as const,
  adminCase: (caseId: string) =>
    ["compliance", "admin", "case", caseId] as const,
  adminCaseDownloadUrls: (caseId: string) =>
    ["compliance", "admin", "case", caseId, "download-urls"] as const,
  adminRequirement: (requirementId: string) =>
    ["compliance", "admin", "requirement", requirementId] as const,
  adminMerchants: () => ["admin", "merchants"] as const,

  documentTypes: (includeInactive: boolean) =>
    ["compliance", "admin", "document-types", includeInactive] as const,
};

export { getAdminMerchantsQueryOptions } from "./admin-merchants-query";

export function getAdminOverviewQueryOptions({
  accessToken,
}: {
  accessToken: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.adminOverview(),
    queryFn: () =>
      complianceApi.admin.getOverview({
        accessToken,
      }),
  });
}

export function getAdminWorkflowStatsQueryOptions({
  accessToken,
}: {
  accessToken: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.adminOverviewWorkflowStats(),
    queryFn: () =>
      complianceApi.admin.getOverviewWorkflowStats({
        accessToken,
      }),
  });
}

export function getMerchantRequiringActionCountQueryOptions({
  accessToken,
  applicationId,
}: {
  accessToken: string;
  applicationId: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.merchantRequiringActionCount(applicationId),
    queryFn: () =>
      complianceApi.merchant.getRequiringActionCount({
        accessToken,
        applicationId,
      }),
    staleTime: 60_000,
    retry: queryRetry((failureCount, error) => {
      if (error instanceof ComplianceApiError) {
        return error.statusCode >= 500 && failureCount < 3;
      }
      return failureCount < 3;
    }),
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 10_000),
  });
}

export function getMerchantCasesInfiniteQueryOptions({
  accessToken,
  applicationId,
  limit = 50,
  workflowStatuses,
}: {
  accessToken: string;
  applicationId: string;
  limit?: number;
  workflowStatuses?: string;
}) {
  const params = {
    applicationId,
    limit,
    workflowStatuses,
  };

  return infiniteQueryOptions({
    queryKey: [...merchantCasesInfiniteQueryKeyPrefix, params] as const,
    queryFn: ({ pageParam, signal }) =>
      complianceApi.merchant.getCases({
        accessToken,
        applicationId,
        limit,
        workflowStatuses,
        cursor: pageParam,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    placeholderData: keepPreviousData,
  });
}

export function getMerchantCaseQueryOptions({
  accessToken,
  applicationId,
  caseId,
}: {
  accessToken: string;
  applicationId: string;
  caseId: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.merchantCase(caseId),
    queryFn: () =>
      complianceApi.merchant.getCaseById({
        accessToken,
        applicationId,
        caseId,
      }),
  });
}

export function getMerchantPaymentCaseQueryOptions({
  accessToken,
  applicationId,
  paymentId,
}: {
  accessToken: string;
  applicationId: string;
  paymentId: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.merchantPaymentCase(applicationId, paymentId),
    queryFn: () =>
      complianceApi.merchant.getPaymentComplianceCase({
        accessToken,
        applicationId,
        paymentId,
      }),
    staleTime: 30_000,
  });
}

export function getAdminCasesQueryOptions({
  accessToken,
  cursor,
  limit,
  merchantId,
  applicationId,
  customerName,
  workflowStatuses,
}: {
  accessToken: string;
  cursor?: string;
  limit?: number;
  merchantId?: string;
  applicationId?: string;
  customerName?: string;
  workflowStatuses?: string;
}) {
  const params = {
    cursor,
    limit,
    merchantId,
    applicationId,
    customerName,
    workflowStatuses,
  };
  return queryOptions({
    queryKey: complianceQueryKeys.adminCases(params),
    queryFn: () =>
      complianceApi.admin.getCases({
        accessToken,
        ...params,
      }),
  });
}

export function getAdminCasesInfiniteQueryOptions({
  accessToken,
  limit = 50,
  merchantId,
  applicationId,
  customerName,
  workflowStatuses,
}: {
  accessToken: string;
  limit?: number;
  merchantId?: string;
  applicationId?: string;
  customerName?: string;
  workflowStatuses?: string;
}) {
  const params = {
    limit,
    merchantId,
    applicationId,
    customerName,
    workflowStatuses,
  };

  return infiniteQueryOptions({
    queryKey: [...adminCasesInfiniteQueryKeyPrefix, params] as const,
    queryFn: ({ pageParam }) =>
      complianceApi.admin.getCases({
        accessToken,
        cursor: pageParam,
        ...params,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    placeholderData: keepPreviousData,
  });
}

export function getAdminCaseQueryOptions({
  accessToken,
  caseId,
}: {
  accessToken: string;
  caseId: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.adminCase(caseId),
    queryFn: () =>
      complianceApi.admin.getCaseById({
        accessToken,
        caseId,
      }),
  });
}

export function getAdminCaseDownloadUrlsQueryOptions({
  accessToken,
  caseId,
}: {
  accessToken: string;
  caseId: string;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.adminCaseDownloadUrls(caseId),
    queryFn: () =>
      complianceApi.admin.getCaseDownloadUrls({
        accessToken,
        caseId,
      }),
    staleTime: 60_000,
  });
}

export function getAdminDocumentTypesQueryOptions({
  accessToken,
  includeInactive,
}: {
  accessToken: string;
  includeInactive: boolean;
}) {
  return queryOptions({
    queryKey: complianceQueryKeys.documentTypes(includeInactive),
    queryFn: () =>
      complianceApi.admin.listDocumentTypes({
        accessToken,
        includeInactive,
      }),
  });
}
